import React, { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import { useSpring, animated } from '@react-spring/three'
import * as THREE from 'three'

const CyberpunkGeometry = ({ position, geometry, scale = 1, rotationSpeed = 0.01 }) => {
  const meshRef = useRef()
  
  // Animated spring for floating effect
  const { positionY } = useSpring({
    from: { positionY: position[1] },
    to: { positionY: position[1] + 0.8 },
    config: { duration: 4000 },
    loop: { reverse: true }
  })

  // Cyberpunk materials with neon glow
  const material = useMemo(() => {
    const mat = new THREE.MeshStandardMaterial({
      color: new THREE.Color('#ff00ff'),
      metalness: 0.9,
      roughness: 0.1,
      emissive: new THREE.Color('#ff00ff'),
      emissiveIntensity: 0.3,
      transparent: true,
      opacity: 0.8,
    })
    
    // Add wireframe overlay for cyberpunk effect
    mat.wireframe = false
    return mat
  }, [])

  const wireframeMaterial = useMemo(() => 
    new THREE.MeshBasicMaterial({
      color: new THREE.Color('#00ffff'),
      wireframe: true,
      transparent: true,
      opacity: 0.4,
    }), []
  )

  // Rotation animation with glitch effects
  useFrame((state, delta) => {
    if (meshRef.current) {
      meshRef.current.rotation.x += rotationSpeed * delta
      meshRef.current.rotation.y += rotationSpeed * delta * 0.7
      meshRef.current.rotation.z += rotationSpeed * delta * 0.3
      
      // Add subtle glitch effect
      const time = state.clock.elapsedTime
      const glitchIntensity = Math.sin(time * 10) * 0.001
      meshRef.current.position.x += glitchIntensity
      meshRef.current.position.z += glitchIntensity * 0.5
    }
  })

  return (
    <group>
      {/* Main geometry with cyberpunk material */}
      <animated.mesh
        ref={meshRef}
        position-x={position[0]}
        position-y={positionY}
        position-z={position[2]}
        scale={scale}
        material={material}
      >
        {geometry}
      </animated.mesh>
      
      {/* Wireframe overlay for cyberpunk effect */}
      <animated.mesh
        position-x={position[0]}
        position-y={positionY}
        position-z={position[2]}
        scale={scale * 1.01}
        material={wireframeMaterial}
      >
        {geometry}
      </animated.mesh>
    </group>
  )
}

export default CyberpunkGeometry
